import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Paper,
  Collapse,
  CircularProgress,
  Alert,
  Button,
  Chip,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  TrendingUp as NextIcon,
  SwapHoriz as PivotIcon,
  MyLocation as TargetIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as AIIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  School as SchoolIcon,
  WorkOutline as WorkIcon,
  EmojiObjects as SkillIcon,
  TrendingUp as GrowthIcon,
  TrendingFlat as ArrowIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CareerPathSelection = ({ formData, updateFormData, onValidationChange }) => {
  const [careerPath, setCareerPath] = useState(formData.careerPath || '');
  const [targetPosition, setTargetPosition] = useState(formData.targetPosition || '');
  const [pivotField, setPivotField] = useState(formData.pivotField || '');
  
  // AI suggestion states
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestedRoles, setSuggestedRoles] = useState(formData.suggestedRoles || null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(formData.selectedSuggestion || null);
  const [suggestionError, setSuggestionError] = useState(null);

  // Tier 1 validation states (GPT-4 Mini feasibility check)
  const [validatingFeasibility, setValidatingFeasibility] = useState(false);
  const [feasibilityResult, setFeasibilityResult] = useState(null);
  const validationTimeoutRef = useRef(null);
  
  // Profile confirmation states (combined into this step)
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [preprocessedData, setPreprocessedData] = useState(formData.preprocessedData || null);
  const [currentRole, setCurrentRole] = useState(formData.currentRole || formData.resumeData?.currentPosition || '');
  const [editingCurrent, setEditingCurrent] = useState(false);
  const [editingTarget, setEditingTarget] = useState(false);
  
  // Determine the effective target role based on path (must be defined before validation)
  const effectiveTargetRole = careerPath === 'next-logical' 
    ? (selectedSuggestion?.title || '')
    : (careerPath === 'pivot' ? pivotField : targetPosition);

  // Determine if validation passed (Tier 1 only during wizard)
  const validationPassed = Boolean(
    // No validation needed yet (no role entered)
    !feasibilityResult ||
    // Feasibility passed: valid job title AND not blocked
    (feasibilityResult.isValidJobTitle !== false &&
     feasibilityResult.recommendation !== 'NOT_RECOMMENDED')
  );

  // Check if validation is blocked (only matters if we've run validation)
  const validationBlocked = feasibilityResult?.recommendation === 'NOT_RECOMMENDED' || 
                            feasibilityResult?.isValidJobTitle === false;

  // Has a valid career path selection
  // For pivot/known-target: requires validation to have passed
  // For next-logical (AI suggestions): no validation needed - AI already vetted
  const hasValidPathSelection = Boolean(
    (careerPath === 'next-logical' && selectedSuggestion) || 
    (careerPath === 'pivot' && pivotField.length > 3 && feasibilityResult && !validationBlocked) ||
    (careerPath === 'known-target' && targetPosition.length > 3 && feasibilityResult && !validationBlocked)
  );

  // Profile confirmation valid
  const profileValid = Boolean(
    preprocessedData &&
    currentRole?.trim().length > 2 &&
    effectiveTargetRole?.trim().length > 2
  );

  // Overall validation - path selection + profile confirmation
  // Allow proceeding if they have valid input AND aren't blocked by validation
  const isValid = Boolean(
    careerPath && 
    !validatingFeasibility &&
    !loadingProfile &&
    hasValidPathSelection &&
    profileValid &&
    !validationBlocked
  );

  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);

  // Tier 1 validation only - GPT-4 Mini feasibility check (cheap)
  // Tier 2 (JSearch) runs AFTER payment during report generation to save costs
  const validateTargetRole = useCallback(async (role, isAISuggested = false) => {
    if (!role || role.length < 3) {
      setFeasibilityResult(null);
      return;
    }

    const functions = getFunctions();

    // Tier 1: Feasibility check (skip for AI-suggested roles)
    if (!isAISuggested) {
      setValidatingFeasibility(true);
      setFeasibilityResult(null);

      try {
        const checkFeasibility = httpsCallable(functions, 'checkPivotFeasibility');
        const result = await checkFeasibility({
          currentRole: formData.resumeData?.currentPosition,
          targetRole: role,
          yearsExperience: formData.resumeData?.yearsExperience,
          skills: formData.resumeData?.skills,
          isAISuggested: false
        });
        
        setFeasibilityResult(result.data);
        
        // GPT-4 Mini also validates if it's a real job title
        // No need for JSearch during wizard - that runs after payment
        if (result.data.isValidJobTitle === false) {
          // Not a real job title - block
          setValidatingFeasibility(false);
          return;
        }
      } catch (error) {
        console.error('Feasibility check error:', error);
        // Allow proceeding on error
        setFeasibilityResult({
          recommendation: 'PROCEED_WITH_CAUTION',
          userMessage: 'Unable to fully assess this transition.',
          error: true
        });
      }
      setValidatingFeasibility(false);
    } else {
      // For AI-suggested roles, mark as skipped/passed
      setFeasibilityResult({
        skipped: true,
        recommendation: 'PROCEED',
        isValidJobTitle: true
      });
    }
    // NOTE: Tier 2 (JSearch market validation) runs AFTER payment
    // during analyzeCareerPath to save API costs
  }, [formData.resumeData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  // Run validation when user clicks "Validate" button (not on every keystroke)
  // ONLY save targetRole to formData AFTER validation passes to prevent truncation
  const handleValidateRole = useCallback(async () => {
    const roleToValidate = careerPath === 'pivot' ? pivotField : targetPosition;
    if (roleToValidate && roleToValidate.length > 3) {
      // Don't save targetRole yet - wait for validation to pass
      await validateTargetRole(roleToValidate, false);
      // After validation completes, check if it passed and only THEN save
      // The actual save happens in a useEffect that watches feasibilityResult
    }
  }, [careerPath, pivotField, targetPosition, validateTargetRole]);
  
  // Save targetRole to formData ONLY after validation passes
  // NOTE: This only applies to manually-entered roles (pivot/known-target)
  // AI-suggested roles (next-logical) are handled separately in handleSelectSuggestion
  useEffect(() => {
    // Skip for AI-suggested roles - they're handled in handleSelectSuggestion
    if (careerPath === 'next-logical') return;
    if (feasibilityResult?.skipped) return; // AI suggestions have skipped=true
    
    if (feasibilityResult && 
        !feasibilityResult.error &&
        feasibilityResult.isValidJobTitle !== false &&
        feasibilityResult.recommendation !== 'NOT_RECOMMENDED') {
      // Validation passed - NOW it's safe to save the complete targetRole
      const validatedRole = careerPath === 'pivot' ? pivotField : targetPosition;
      if (validatedRole && validatedRole.length > 3) {
        console.log('✅ Validation passed - saving targetRole:', validatedRole);
        updateFormData({
          careerPath,
          pivotField,
          targetPosition,
          targetRole: validatedRole
        });
      }
    }
  }, [feasibilityResult, careerPath, pivotField, targetPosition, updateFormData]);
  
  // Preprocess resume to get profile data
  const preprocessProfile = useCallback(async (forceRefresh = false) => {
    if (!formData.resumeText) return;
    
    // Check if we have FULL preprocessed data (with currentPosition from AI parsing)
    // formData.resumeData from file upload has currentPosition, but pasted text parsing may not
    const hasFullData = formData.preprocessedData?.currentPosition || 
                        (formData.resumeData?.currentPosition && formData.resumeData?.skills?.length > 3);
    
    if (!forceRefresh && hasFullData && formData.preprocessedData) {
      console.log('📋 Using existing preprocessed data with currentPosition:', formData.preprocessedData.currentPosition);
      setPreprocessedData(formData.preprocessedData);
      if (formData.preprocessedData.currentPosition && !currentRole) {
        setCurrentRole(formData.preprocessedData.currentPosition);
      }
      return;
    }
    
    if (preprocessedData?.currentPosition && !forceRefresh) {
      console.log('📋 Already have preprocessed data with currentPosition:', preprocessedData.currentPosition);
      return;
    }
    
    setLoadingProfile(true);
    setProfileError(null);
    
    try {
      console.log('🔍 Calling preprocessCareerResume cloud function for full parsing...');
      const functions = getFunctions();
      const preprocessResume = httpsCallable(functions, 'preprocessCareerResume');
      
      const result = await preprocessResume({
        resumeText: formData.resumeText,
        careerPath: careerPath,
        targetPosition: effectiveTargetRole
      });
      
      console.log('✅ Pre-processing complete:', result.data);
      const data = result.data.data;
      setPreprocessedData(data);
      
      // Set current role from parsed data
      if (data.currentPosition) {
        console.log('📝 Setting currentRole from AI parsing:', data.currentPosition);
        setCurrentRole(data.currentPosition);
      }
      
      // Update form data - DO NOT set targetRole here
      // targetRole is only set via:
      //   1. handleSuggestionSelect (for AI-suggested roles)
      //   2. validation useEffect (for manually-entered roles after validation passes)
      updateFormData({
        preprocessedData: data,
        resumeData: data,
        currentRole: data.currentPosition || currentRole
        // NOTE: targetRole is NOT set here to prevent overwriting with stale values
      });
      
    } catch (err) {
      console.error('❌ Pre-processing error:', err);
      setProfileError(err.message || 'Failed to process resume. Please try again.');
    } finally {
      setLoadingProfile(false);
    }
  }, [formData.resumeText, formData.resumeData, formData.preprocessedData, careerPath, effectiveTargetRole, preprocessedData, currentRole, updateFormData]);
  
  // Auto-trigger preprocessing when we have a valid path + target
  useEffect(() => {
    const hasValidSelection = Boolean(
      (careerPath === 'next-logical' && selectedSuggestion) ||
      (careerPath === 'pivot' && pivotField.length > 3 && feasibilityResult?.recommendation !== 'NOT_RECOMMENDED') ||
      (careerPath === 'known-target' && targetPosition.length > 3 && feasibilityResult?.recommendation !== 'NOT_RECOMMENDED')
    );
    
    if (hasValidSelection && formData.resumeText && !preprocessedData && !loadingProfile) {
      preprocessProfile();
    }
  }, [careerPath, selectedSuggestion, pivotField, targetPosition, feasibilityResult, formData.resumeText, preprocessedData, loadingProfile, preprocessProfile]);
  
  // Sync currentRole when preprocessedData loads
  useEffect(() => {
    if (preprocessedData?.currentPosition && !currentRole) {
      console.log('📝 Setting current role from preprocessed data:', preprocessedData.currentPosition);
      setCurrentRole(preprocessedData.currentPosition);
    }
  }, [preprocessedData, currentRole]);
  
  // Also sync from formData.resumeData if available (from resume upload step)
  useEffect(() => {
    if (formData.resumeData?.currentPosition && !currentRole) {
      console.log('📝 Setting current role from resume data:', formData.resumeData.currentPosition);
      setCurrentRole(formData.resumeData.currentPosition);
    }
  }, [formData.resumeData, currentRole]);
  
  // Update form data when current role changes
  useEffect(() => {
    if (currentRole && preprocessedData) {
      updateFormData({
        currentRole: currentRole.trim()
      });
    }
  }, [currentRole, preprocessedData, updateFormData]);

  // Fetch AI suggestions when "next-logical" is selected
  const fetchAISuggestions = useCallback(async () => {
    if (!formData.resumeText || !formData.resumeData) {
      setSuggestionError('Please complete the resume upload step first');
      return;
    }

    setLoadingSuggestions(true);
    setSuggestionError(null);

    try {
      const functions = getFunctions();
      const suggestCareerPaths = httpsCallable(functions, 'suggestCareerPaths');
      
      const result = await suggestCareerPaths({
        resumeText: formData.resumeText,
        resumeData: formData.resumeData
      });

      if (result.data && result.data.suggestions) {
        setSuggestedRoles(result.data.suggestions);
        updateFormData({
          careerPath: 'next-logical',
          suggestedRoles: result.data.suggestions
        });
      } else {
        throw new Error('No suggestions received');
      }
    } catch (error) {
      console.error('Error fetching career suggestions:', error);
      setSuggestionError('Failed to generate career suggestions. Please try again or choose another option.');
    } finally {
      setLoadingSuggestions(false);
    }
  }, [formData.resumeText, formData.resumeData, updateFormData]);

  // Handle selecting a suggested role
  const handleSuggestionSelect = useCallback((suggestion) => {
    console.log('🎯 AI Suggestion selected:', suggestion.title);
    setSelectedSuggestion(suggestion);
    updateFormData({
      careerPath: 'next-logical',
      selectedSuggestion: suggestion,
      targetPosition: suggestion.title,
      targetRole: suggestion.title, // Ensure targetRole is always set
      suggestedRoles
    });
    console.log('✅ formData updated with targetRole:', suggestion.title);
    
    // Run Tier 2 validation only (skip Tier 1 for AI suggestions)
    validateTargetRole(suggestion.title, true);
  }, [suggestedRoles, updateFormData, validateTargetRole]);

  // Handle career path change
  const handleCareerPathChange = useCallback((event) => {
    const value = event.target.value;
    setCareerPath(value);
    
    // Clear validation when switching paths
    setFeasibilityResult(null);
    
    // If selecting "next-logical", fetch AI suggestions
    if (value === 'next-logical' && !suggestedRoles) {
      fetchAISuggestions();
    }
    
    updateFormData({
      careerPath: value,
      targetPosition: value === 'known-target' ? targetPosition : null,
      pivotField: value === 'pivot' ? pivotField : null,
      selectedSuggestion: value === 'next-logical' ? selectedSuggestion : null
    });
  }, [targetPosition, pivotField, suggestedRoles, selectedSuggestion, fetchAISuggestions, updateFormData]);

  // Handle target position change - NO auto-validation, just update state
  const handleTargetPositionChange = useCallback((event) => {
    const value = event.target.value;
    setTargetPosition(value);
    
    // Clear previous validation when typing (user needs to re-validate)
    if (feasibilityResult) {
      setFeasibilityResult(null);
    }
    
    // ALWAYS sync targetRole with current value to prevent stale data bug
    updateFormData({
      careerPath,
      targetPosition: value
      // NOTE: targetRole is NOT saved here - only saved after validation passes
    });
  }, [careerPath, updateFormData, feasibilityResult]);

  // Handle pivot field change - NO auto-validation, just update state
  const handlePivotFieldChange = useCallback((event) => {
    const value = event.target.value;
    setPivotField(value);
    
    // Clear previous validation when typing (user needs to re-validate)
    if (feasibilityResult) {
      setFeasibilityResult(null);
    }
    
    // ALWAYS sync targetRole with current value to prevent stale data bug
    updateFormData({
      careerPath,
      pivotField: value
      // NOTE: targetRole is NOT saved here - only saved after validation passes
    });
  }, [careerPath, updateFormData, feasibilityResult]);

  // Career path options
  const pathOptions = [
    {
      value: 'next-logical',
      icon: <NextIcon sx={{ fontSize: 32, color: '#00FFF5' }} />,
      title: 'Next Logical Step',
      description: 'Discover your natural next career move based on your experience and skills',
      examples: 'Examples: Software Developer → Senior Software Developer, Analyst → Senior Analyst'
    },
    {
      value: 'pivot',
      icon: <PivotIcon sx={{ fontSize: 32, color: '#9D00FF' }} />,
      title: 'Pivot to New Field',
      description: 'Want to transition to a different role or industry? We\'ll identify your transferable skills',
      examples: 'Examples: Teacher → Corporate Trainer, Engineer → Product Manager'
    },
    {
      value: 'known-target',
      icon: <TargetIcon sx={{ fontSize: 32, color: '#00FFF5' }} />,
      title: 'I Know My Target',
      description: 'You already know exactly what position you want next',
      examples: 'Examples: "Engineering Manager", "Director of Marketing", "Data Scientist"'
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" sx={{ color: '#fafafa', mb: 1, fontWeight: 600 }}>
        Choose Your Career Path
      </Typography>
      <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 4 }}>
        Help us understand your career goals so we can create a tailored development plan
      </Typography>

      {/* Career Path Options */}
      <FormControl component="fieldset" fullWidth>
        <RadioGroup value={careerPath} onChange={handleCareerPathChange}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pathOptions.map((option) => (
              <Paper
                key={option.value}
                sx={{
                  p: 3,
                  backgroundColor: careerPath === option.value ? '#171717' : '#0a0a0a',
                  border: `2px solid ${careerPath === option.value ? '#00FFF5' : '#262626'}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: careerPath === option.value ? '#00FFF5' : '#525252',
                    backgroundColor: '#171717'
                  }
                }}
                onClick={() => {
                  if (careerPath !== option.value) {
                    handleCareerPathChange({ target: { value: option.value } });
                  }
                }}
              >
                <FormControlLabel
                  value={option.value}
                  control={
                    <Radio 
                      sx={{
                        color: '#525252',
                        '&.Mui-checked': {
                          color: '#00FFF5'
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                      <Box sx={{ mt: 0.5 }}>
                        {option.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5 }}>
                          {option.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 1 }}>
                          {option.description}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#737373', fontStyle: 'italic' }}>
                          {option.examples}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{
                    m: 0,
                    alignItems: 'flex-start',
                    width: '100%'
                  }}
                />
              </Paper>
            ))}
          </Box>
        </RadioGroup>
      </FormControl>

      {/* Conditional Fields */}
      
      {/* Pivot Field Input */}
      <Collapse in={careerPath === 'pivot'}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#fafafa', mb: 2, fontWeight: 600 }}>
            What field or role do you want to pivot to?
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., Product Management, Data Science, UX Design, Technical Writing..."
            value={pivotField}
            onChange={handlePivotFieldChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0a0a0a',
                '& fieldset': {
                  borderColor: '#262626',
                  borderWidth: 2
                },
                '&:hover fieldset': {
                  borderColor: '#525252'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9D00FF'
                }
              },
              '& .MuiInputBase-input': {
                color: '#fafafa'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: '#737373', display: 'block', mt: 1 }}>
            💡 Tip: Be as specific as possible. Include the role title and industry if applicable.
          </Typography>
          
          {/* Check Feasibility Button - only show if not yet validated */}
          {pivotField.length > 3 && !feasibilityResult && !validatingFeasibility && (
            <Button
              variant="outlined"
              onClick={handleValidateRole}
              sx={{ 
                mt: 2, 
                color: '#00FFF5', 
                borderColor: '#00FFF5',
                '&:hover': { borderColor: '#00CCC5', bgcolor: 'rgba(0,255,245,0.1)' }
              }}
            >
              Check Career Transition
            </Button>
          )}
          
          {/* Validation in Progress */}
          {validatingFeasibility && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress sx={{ bgcolor: '#262626', '& .MuiLinearProgress-bar': { bgcolor: '#00FFF5' } }} />
              <Typography variant="caption" sx={{ color: '#a1a1aa', mt: 1, display: 'block' }}>
                Analyzing career transition feasibility...
              </Typography>
            </Box>
          )}
          
          {/* Tiered Feasibility Feedback based on skill overlap */}
          {feasibilityResult && !validatingFeasibility && careerPath === 'pivot' && feasibilityResult.recommendation !== 'NOT_RECOMMENDED' && (() => {
            const overlap = feasibilityResult.skillOverlap?.percentage || 0;
            const timeline = feasibilityResult.feasibility?.estimatedTimeToQualify || 'Unknown';
            
            // 50%+ = Great match (green)
            if (overlap >= 50) {
              return (
                <Alert 
                  severity="success" 
                  icon={<CheckIcon />}
                  sx={{ mt: 2, bgcolor: '#171717', border: '1px solid #22c55e', '& .MuiAlert-icon': { color: '#22c55e' } }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                    Strong Match!
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                    {feasibilityResult.userMessage}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#22c55e', mt: 1, display: 'block' }}>
                    Skill Overlap: {overlap}% • Timeline: {timeline}
                  </Typography>
                </Alert>
              );
            }
            
            // 30-50% = Good foundation (blue info)
            if (overlap >= 30) {
              return (
                <Alert 
                  severity="info" 
                  icon={<AIIcon />}
                  sx={{ mt: 2, bgcolor: '#171717', border: '1px solid #3b82f6', '& .MuiAlert-icon': { color: '#3b82f6' } }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                    Career Transition Insight
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                    {feasibilityResult.userMessage}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#3b82f6', mt: 1, display: 'block' }}>
                    Skill Overlap: {overlap}% • Timeline: {timeline}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373', mt: 0.5, display: 'block' }}>
                    Your report will include a training plan, timeline, and budget recommendations.
                  </Typography>
                </Alert>
              );
            }
            
            // 10-30% = Achievable with significant effort (neutral info)
            if (overlap >= 10) {
              return (
                <Alert 
                  severity="info" 
                  icon={<AIIcon />}
                  sx={{ mt: 2, bgcolor: '#171717', border: '1px solid #8b5cf6', '& .MuiAlert-icon': { color: '#8b5cf6' } }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                    Significant Career Change
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                    {feasibilityResult.userMessage}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#8b5cf6', mt: 1, display: 'block' }}>
                    Skill Overlap: {overlap}% • Timeline: {timeline}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373', mt: 0.5, display: 'block' }}>
                    This transition requires dedicated training and time investment. Your report will outline the specific steps, certifications, and budget needed.
                  </Typography>
                </Alert>
              );
            }
            
            // <10% = Major transition (informative, not alarming)
            return (
              <Alert 
                severity="info" 
                icon={<AIIcon />}
                sx={{ mt: 2, bgcolor: '#171717', border: '1px solid #f59e0b', '& .MuiAlert-icon': { color: '#f59e0b' } }}
              >
                <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                  Major Career Transition
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                  {feasibilityResult.userMessage}
                </Typography>
                <Typography variant="caption" sx={{ color: '#f59e0b', mt: 1, display: 'block' }}>
                  Skill Overlap: {overlap}% • Timeline: {timeline}
                </Typography>
                <Typography variant="caption" sx={{ color: '#737373', mt: 0.5, display: 'block' }}>
                  This is a substantial career change requiring training, certifications, and time commitment. Your report will provide a detailed roadmap.
                </Typography>
                {feasibilityResult.suggestedAlternatives?.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#737373' }}>
                      You might also consider:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      {feasibilityResult.suggestedAlternatives.slice(0, 3).map((alt, i) => (
                        <Chip key={i} label={alt} size="small" onClick={() => { setPivotField(alt); setFeasibilityResult(null); }}
                          sx={{ bgcolor: '#262626', color: '#00FFF5', cursor: 'pointer', fontSize: '0.7rem', '&:hover': { bgcolor: '#333' } }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Alert>
            );
          })()}
          
          {/* Only show error for truly unrealistic transitions (fictional jobs, etc.) */}
          {feasibilityResult?.recommendation === 'NOT_RECOMMENDED' && !validatingFeasibility && careerPath === 'pivot' && (
            <Alert 
              severity="error" 
              icon={<ErrorIcon />}
              sx={{ mt: 2, bgcolor: '#0a0a0a', border: '1px solid #ef4444', '& .MuiAlert-icon': { color: '#ef4444' } }}
            >
              <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                Unable to Create Career Plan
              </Typography>
              <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                {feasibilityResult.userMessage}
              </Typography>
              {feasibilityResult.suggestedAlternatives?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Try one of these related roles:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {feasibilityResult.suggestedAlternatives.map((alt, i) => (
                      <Chip key={i} label={alt} size="small" onClick={() => { setPivotField(alt); setFeasibilityResult(null); }}
                        sx={{ bgcolor: '#171717', color: '#00FFF5', cursor: 'pointer', '&:hover': { bgcolor: '#262626' } }} />
                    ))}
                  </Box>
                </Box>
              )}
            </Alert>
          )}
        </Box>
      </Collapse>

      {/* Known Target Input */}
      <Collapse in={careerPath === 'known-target'}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#fafafa', mb: 2, fontWeight: 600 }}>
            What's your target position?
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., Senior Software Engineer, Engineering Manager, VP of Sales..."
            value={targetPosition}
            onChange={handleTargetPositionChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0a0a0a',
                '& fieldset': {
                  borderColor: '#262626',
                  borderWidth: 2
                },
                '&:hover fieldset': {
                  borderColor: '#525252'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00FFF5'
                }
              },
              '& .MuiInputBase-input': {
                color: '#fafafa'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: '#737373', display: 'block', mt: 1 }}>
            💡 Tip: Use the exact job title you're aiming for to get the most accurate career plan.
          </Typography>
          
          {/* Check Feasibility Button - only show if not yet validated */}
          {targetPosition.length > 3 && !feasibilityResult && !validatingFeasibility && (
            <Button
              variant="outlined"
              onClick={handleValidateRole}
              sx={{ 
                mt: 2, 
                color: '#00FFF5', 
                borderColor: '#00FFF5',
                '&:hover': { borderColor: '#00CCC5', bgcolor: 'rgba(0,255,245,0.1)' }
              }}
            >
              Check Career Transition
            </Button>
          )}
          
          {/* Validation in Progress */}
          {validatingFeasibility && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress sx={{ bgcolor: '#262626', '& .MuiLinearProgress-bar': { bgcolor: '#00FFF5' } }} />
              <Typography variant="caption" sx={{ color: '#a1a1aa', mt: 1, display: 'block' }}>
                Analyzing career transition feasibility...
              </Typography>
            </Box>
          )}
          
          {/* Tiered Feasibility Feedback based on skill overlap */}
          {feasibilityResult && !validatingFeasibility && careerPath === 'known-target' && feasibilityResult.recommendation !== 'NOT_RECOMMENDED' && (() => {
            const overlap = feasibilityResult.skillOverlap?.percentage || 0;
            const timeline = feasibilityResult.feasibility?.estimatedTimeToQualify || 'Unknown';
            
            // 50%+ = Great match (green)
            if (overlap >= 50) {
              return (
                <Alert 
                  severity="success" 
                  icon={<CheckIcon />}
                  sx={{ mt: 2, bgcolor: '#171717', border: '1px solid #22c55e', '& .MuiAlert-icon': { color: '#22c55e' } }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                    Strong Match!
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                    {feasibilityResult.userMessage}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#22c55e', mt: 1, display: 'block' }}>
                    Skill Overlap: {overlap}% • Timeline: {timeline}
                  </Typography>
                </Alert>
              );
            }
            
            // 30-50% = Good foundation (blue info)
            if (overlap >= 30) {
              return (
                <Alert 
                  severity="info" 
                  icon={<AIIcon />}
                  sx={{ mt: 2, bgcolor: '#171717', border: '1px solid #3b82f6', '& .MuiAlert-icon': { color: '#3b82f6' } }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                    Career Transition Insight
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                    {feasibilityResult.userMessage}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#3b82f6', mt: 1, display: 'block' }}>
                    Skill Overlap: {overlap}% • Timeline: {timeline}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373', mt: 0.5, display: 'block' }}>
                    Your report will include a training plan, timeline, and budget recommendations.
                  </Typography>
                </Alert>
              );
            }
            
            // 10-30% = Achievable with significant effort (neutral info)
            if (overlap >= 10) {
              return (
                <Alert 
                  severity="info" 
                  icon={<AIIcon />}
                  sx={{ mt: 2, bgcolor: '#171717', border: '1px solid #8b5cf6', '& .MuiAlert-icon': { color: '#8b5cf6' } }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                    Significant Career Change
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                    {feasibilityResult.userMessage}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#8b5cf6', mt: 1, display: 'block' }}>
                    Skill Overlap: {overlap}% • Timeline: {timeline}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373', mt: 0.5, display: 'block' }}>
                    This transition requires dedicated training and time investment. Your report will outline the specific steps, certifications, and budget needed.
                  </Typography>
                </Alert>
              );
            }
            
            // <10% = Major transition (informative, not alarming)
            return (
              <Alert 
                severity="info" 
                icon={<AIIcon />}
                sx={{ mt: 2, bgcolor: '#171717', border: '1px solid #f59e0b', '& .MuiAlert-icon': { color: '#f59e0b' } }}
              >
                <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                  Major Career Transition
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                  {feasibilityResult.userMessage}
                </Typography>
                <Typography variant="caption" sx={{ color: '#f59e0b', mt: 1, display: 'block' }}>
                  Skill Overlap: {overlap}% • Timeline: {timeline}
                </Typography>
                <Typography variant="caption" sx={{ color: '#737373', mt: 0.5, display: 'block' }}>
                  This is a substantial career change requiring training, certifications, and time commitment. Your report will provide a detailed roadmap.
                </Typography>
                {feasibilityResult.suggestedAlternatives?.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#737373' }}>
                      You might also consider:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      {feasibilityResult.suggestedAlternatives.slice(0, 3).map((alt, i) => (
                        <Chip key={i} label={alt} size="small" onClick={() => { setTargetPosition(alt); setFeasibilityResult(null); }}
                          sx={{ bgcolor: '#262626', color: '#00FFF5', cursor: 'pointer', fontSize: '0.7rem', '&:hover': { bgcolor: '#333' } }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Alert>
            );
          })()}
          
          {/* Only show error for truly unrealistic transitions (fictional jobs, etc.) */}
          {feasibilityResult?.recommendation === 'NOT_RECOMMENDED' && !validatingFeasibility && careerPath === 'known-target' && (
            <Alert 
              severity="error" 
              icon={<ErrorIcon />}
              sx={{ mt: 2, bgcolor: '#0a0a0a', border: '1px solid #ef4444', '& .MuiAlert-icon': { color: '#ef4444' } }}
            >
              <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                Unable to Create Career Plan
              </Typography>
              <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                {feasibilityResult.userMessage}
              </Typography>
              {feasibilityResult.suggestedAlternatives?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Try one of these related roles:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {feasibilityResult.suggestedAlternatives.map((alt, i) => (
                      <Chip key={i} label={alt} size="small" onClick={() => { setTargetPosition(alt); setFeasibilityResult(null); }}
                        sx={{ bgcolor: '#171717', color: '#00FFF5', cursor: 'pointer', '&:hover': { bgcolor: '#262626' } }} />
                    ))}
                  </Box>
                </Box>
              )}
            </Alert>
          )}
        </Box>
      </Collapse>

      {/* Next Logical - AI Suggestions */}
      <Collapse in={careerPath === 'next-logical'}>
        <Box sx={{ mt: 3 }}>
          {/* Loading State */}
          {loadingSuggestions && (
            <Paper
              sx={{
                p: 4,
                backgroundColor: '#0a0a0a',
                border: '1px solid rgba(0,255,245,0.2)',
                textAlign: 'center'
              }}
            >
              <CircularProgress sx={{ color: '#00FFF5', mb: 2 }} />
              <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                Finding the best next career steps for you...
              </Typography>
            </Paper>
          )}

          {/* Error State */}
          {suggestionError && !loadingSuggestions && (
            <Alert 
              severity="error"
              sx={{
                backgroundColor: '#0a0a0a',
                border: '1px solid #ef4444',
                color: '#fafafa'
              }}
              action={
                <Button
                  size="small"
                  onClick={fetchAISuggestions}
                  sx={{ color: '#00FFF5' }}
                >
                  Retry
                </Button>
              }
            >
              {suggestionError}
            </Alert>
          )}

          {/* Suggested Roles */}
          {suggestedRoles && !loadingSuggestions && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AIIcon sx={{ color: '#00FFF5' }} />
                <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                  Recommended Career Paths
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 3 }}>
                Based on your experience and skills, here are your most logical next steps:
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {suggestedRoles.map((suggestion, index) => (
                  <Paper
                    key={index}
                    onClick={() => handleSuggestionSelect(suggestion)}
                    sx={{
                      p: 3,
                      backgroundColor: selectedSuggestion?.title === suggestion.title ? '#171717' : '#0a0a0a',
                      border: `2px solid ${selectedSuggestion?.title === suggestion.title ? '#00FFF5' : '#262626'}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#00FFF5',
                        backgroundColor: '#171717'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {selectedSuggestion?.title === suggestion.title && (
                        <CheckIcon sx={{ color: '#00FFF5', mt: 0.5 }} />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 600, mb: 1 }}>
                          {suggestion.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
                          {suggestion.justification}
                        </Typography>
                        {suggestion.requiredSkills && suggestion.requiredSkills.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {suggestion.requiredSkills.slice(0, 5).map((skill, idx) => (
                              <Chip
                                key={idx}
                                label={skill}
                                size="small"
                                sx={{
                                  backgroundColor: '#171717',
                                  color: '#00FFF5',
                                  fontSize: '0.75rem'
                                }}
                              />
                            ))}
                            {suggestion.requiredSkills.length > 5 && (
                              <Chip
                                label={`+${suggestion.requiredSkills.length - 5} more`}
                                size="small"
                                sx={{
                                  backgroundColor: '#171717',
                                  color: '#9D00FF',
                                  fontSize: '0.75rem'
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>

              <Typography variant="caption" sx={{ color: '#737373', display: 'block', mt: 2 }}>
                💡 Select the role that best matches your career goals
              </Typography>
              
            </Box>
          )}
        </Box>
      </Collapse>

      {/* ========== PROFILE CONFIRMATION SECTION ========== */}
      {/* Shows after user has made a valid career path selection */}
      <Collapse in={hasValidPathSelection && !validatingFeasibility}>
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ borderColor: '#262626', mb: 4 }} />
          
          {/* Loading Profile */}
          {loadingProfile && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#00FFF5', mb: 2 }} size={40} />
              <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                Preparing your profile...
              </Typography>
            </Box>
          )}
          
          {/* Profile Error */}
          {profileError && !loadingProfile && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              action={
                <Button size="small" onClick={() => preprocessProfile(true)} sx={{ color: '#fff' }}>
                  Retry
                </Button>
              }
            >
              {profileError}
            </Alert>
          )}
          
          {/* Profile Content */}
          {preprocessedData && !loadingProfile && (
            <Box>
              {/* Header */}
              <Typography variant="h6" sx={{ color: '#fafafa', mb: 1, fontWeight: 600 }}>
                Confirm Your Career Transition
              </Typography>
              <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 3 }}>
                Review your profile and confirm the transition
              </Typography>

              {/* Career Transition Card */}
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#0a0a0a', border: '2px solid rgba(0,255,245,0.3)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#00FFF5', fontWeight: 700, mb: 3, fontSize: '1rem' }}>
                  Your Career Path
                </Typography>

                {/* Current Role */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: '#737373', textTransform: 'uppercase', letterSpacing: 1 }}>
                      Current/Previous Role
                    </Typography>
                    {!editingCurrent && currentRole && (
                      <EditIcon 
                        sx={{ color: '#737373', cursor: 'pointer', fontSize: 16, '&:hover': { color: '#00FFF5' } }}
                        onClick={() => setEditingCurrent(true)}
                      />
                    )}
                  </Box>
                  
                  {editingCurrent || !currentRole ? (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., Software Developer, Marketing Coordinator..."
                      value={currentRole}
                      onChange={(e) => setCurrentRole(e.target.value)}
                      onBlur={() => currentRole && setEditingCurrent(false)}
                      autoFocus={editingCurrent}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#171717',
                          '& fieldset': { borderColor: '#262626' },
                          '&.Mui-focused fieldset': { borderColor: '#00FFF5' }
                        },
                        '& .MuiInputBase-input': { color: '#fafafa' }
                      }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ color: '#fafafa', cursor: 'pointer' }} onClick={() => setEditingCurrent(true)}>
                      {currentRole}
                    </Typography>
                  )}
                </Box>

                {/* Arrow */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
                  <Divider sx={{ flex: 1, borderColor: '#262626' }} />
                  <ArrowIcon sx={{ mx: 2, color: '#9D00FF', fontSize: 28 }} />
                  <Divider sx={{ flex: 1, borderColor: '#262626' }} />
                </Box>

                {/* Target Role */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: '#737373', textTransform: 'uppercase', letterSpacing: 1 }}>
                      Target Role
                    </Typography>
                    {!editingTarget && effectiveTargetRole && (
                      <EditIcon 
                        sx={{ color: '#737373', cursor: 'pointer', fontSize: 16, '&:hover': { color: '#9D00FF' } }}
                        onClick={() => setEditingTarget(true)}
                      />
                    )}
                  </Box>
                  
                  {editingTarget ? (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., Senior Software Engineer, Marketing Manager..."
                      value={effectiveTargetRole}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        // Update local state only - don't save targetRole until validation passes
                        if (careerPath === 'pivot') {
                          setPivotField(newValue);
                        } else if (careerPath === 'known-target') {
                          setTargetPosition(newValue);
                        }
                        // Only update local field values, NOT targetRole
                        // targetRole will be saved after validation passes
                        updateFormData({
                          careerPath,
                          pivotField: careerPath === 'pivot' ? newValue : pivotField,
                          targetPosition: careerPath === 'known-target' ? newValue : targetPosition
                          // NOTE: targetRole is NOT saved here - only after validation passes
                        });
                        // Clear validation when role changes
                        if (feasibilityResult) {
                          setFeasibilityResult(null);
                        }
                      }}
                      onBlur={() => effectiveTargetRole && setEditingTarget(false)}
                      autoFocus
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#171717',
                          '& fieldset': { borderColor: '#262626' },
                          '&.Mui-focused fieldset': { borderColor: '#9D00FF' }
                        },
                        '& .MuiInputBase-input': { color: '#fafafa' }
                      }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ color: '#fafafa', cursor: 'pointer' }} onClick={() => setEditingTarget(true)}>
                      {effectiveTargetRole}
                    </Typography>
                  )}
                </Box>

                {/* Validation Warning */}
                {(!currentRole || currentRole.trim().length < 3) && (
                  <Alert severity="warning" sx={{ mt: 2, backgroundColor: '#171717', border: '1px solid #f59e0b' }}>
                    Please confirm your current role
                  </Alert>
                )}
              </Paper>

              {/* Profile Summary */}
              <Typography variant="subtitle2" sx={{ color: '#737373', mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Your Profile Summary
              </Typography>

              <Alert 
                severity="success"
                icon={<CheckIcon />}
                sx={{ mb: 2, backgroundColor: '#0a0a0a', border: '1px solid rgba(0,255,245,0.2)', color: '#fafafa', '& .MuiAlert-icon': { color: '#00FFF5' } }}
              >
                Profile ready! {preprocessedData.skills?.length || 0} skills found, {preprocessedData.yearsExperience || 0}+ years experience.
              </Alert>

              {/* Compact Profile Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                {/* Experience */}
                <Paper sx={{ p: 2, bgcolor: '#0a0a0a', border: '1px solid #262626' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WorkIcon sx={{ color: '#00FFF5', fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: '#00FFF5', fontWeight: 600, textTransform: 'uppercase' }}>
                      Experience
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    {preprocessedData.yearsExperience || 0}+ years
                  </Typography>
                </Paper>

                {/* Education */}
                <Paper sx={{ p: 2, bgcolor: '#0a0a0a', border: '1px solid #262626' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SchoolIcon sx={{ color: '#00FFF5', fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: '#00FFF5', fontWeight: 600, textTransform: 'uppercase' }}>
                      Education
                    </Typography>
                  </Box>
                  {Array.isArray(preprocessedData.education) && preprocessedData.education.length > 0 ? (
                    <Typography variant="body2" sx={{ color: '#fafafa' }}>
                      {preprocessedData.education[0]?.degree} {preprocessedData.education[0]?.major && `in ${preprocessedData.education[0].major}`}
                    </Typography>
                  ) : typeof preprocessedData.education === 'string' ? (
                    <Typography variant="body2" sx={{ color: '#fafafa' }}>{preprocessedData.education}</Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#737373' }}>Not specified</Typography>
                  )}
                </Paper>
              </Box>

              {/* Core Skills */}
              {preprocessedData.coreSkills && preprocessedData.coreSkills.length > 0 && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#0a0a0a', border: '1px solid #262626' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SkillIcon sx={{ color: '#9D00FF', fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: '#9D00FF', fontWeight: 600, textTransform: 'uppercase' }}>
                      Core Skills
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {preprocessedData.coreSkills.slice(0, 8).map((skill, idx) => (
                      <Chip key={idx} label={skill} size="small" sx={{ bgcolor: '#171717', color: '#fafafa', border: '1px solid #9D00FF', fontSize: '0.75rem' }} />
                    ))}
                    {preprocessedData.coreSkills.length > 8 && (
                      <Chip label={`+${preprocessedData.coreSkills.length - 8} more`} size="small" sx={{ bgcolor: '#171717', color: '#9D00FF', fontSize: '0.75rem' }} />
                    )}
                  </Box>
                </Paper>
              )}

              {/* Certifications */}
              {preprocessedData.certifications && preprocessedData.certifications.length > 0 && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#0a0a0a', border: '1px solid #262626' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <GrowthIcon sx={{ color: '#9D00FF', fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: '#9D00FF', fontWeight: 600, textTransform: 'uppercase' }}>
                      Certifications
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {preprocessedData.certifications.slice(0, 5).map((cert, idx) => (
                      <Chip key={idx} label={cert} size="small" sx={{ bgcolor: '#171717', color: '#fafafa', fontSize: '0.75rem' }} />
                    ))}
                  </Box>
                </Paper>
              )}

              {/* Ready to Proceed */}
              {profileValid && (
                <Alert 
                  severity="info"
                  icon={<CheckIcon />}
                  sx={{ mt: 3, backgroundColor: '#0a0a0a', border: '1px solid rgba(0,255,245,0.3)', color: '#fafafa', '& .MuiAlert-icon': { color: '#00FFF5' } }}
                >
                  <Typography variant="body2">
                    Ready! We'll analyze what employers are looking for in <strong>{effectiveTargetRole}</strong> positions and create a personalized plan for your transition from <strong>{currentRole}</strong>.
                  </Typography>
                </Alert>
              )}

              {/* Refresh Button */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => preprocessProfile(true)}
                  disabled={loadingProfile}
                  sx={{ color: '#737373', borderColor: '#262626', fontSize: '0.75rem', '&:hover': { borderColor: '#00FFF5', color: '#00FFF5' } }}
                >
                  Refresh Profile
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CareerPathSelection;

